import {
  ChangeDetectionStrategy,
  Component,
  inject,
} from '@angular/core';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  AppSvgIconComponent,
  ColumnDef,
  OverlayService,
  SignalButton,
  SignalDataTableComponent,
  State,
  TableActionEvent,
  TableStateEvent,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { ConfirmOverlayComponent } from '@core/components/confirm-overlay/confirm-overlay';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import type { DeleteDataLibraryCollectionApiResponse } from '@core/api/workspace/models/deleteDataLibraryCollectionApiResponse';
import type { ListDataLibraryApiResponse } from '@core/api/workspace/models/listDataLibraryApiResponse';
import {
  CreateVariableOverlayComponent,
  type CreateVariableOverlayResult,
  type CreateVariableValueType,
} from './create-variable-overlay/create-variable-overlay';
import {
  EditVariableOverlayComponent,
  type EditVariableOverlayResult,
  type EditVariableValueType,
} from './edit-variable-overlay/edit-variable-overlay';
import {
  DeleteVariableOverlayComponent,
  type DeleteVariableOverlayResult,
} from './delete-variable-overlay/delete-variable-overlay';import { DataLibraryTypeCellComponent } from './data-library-type-cell';
import { DataLibraryKeyCellComponent } from './data-library-key-cell';
import { DataLibraryValueCellComponent } from './data-library-value-cell';
import { dataLibraryTypeIconClass } from './data-library-type-styles';

export type DataLibraryNewTypeId =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'collection';

interface DataLibraryNewTypeItem {
  id: DataLibraryNewTypeId;
  label: string;
  iconSrc: string;
  iconClass: string;
}

const VARIABLE_TYPES = new Set<DataLibraryNewTypeId>([
  'text',
  'number',
  'date',
  'boolean',
]);

@Component({
  selector: 'app-data-library',
  imports: [
    PageHeaderComponent,
    SignalButton,
    SignalDataTableComponent,
    AppSvgIconComponent,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
  ],
  templateUrl: './data-library-page.html',
  styleUrl: './data-library-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataLibraryPage extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly tableState = new State<ListDataLibraryApiResponse>();
  readonly deleteCollectionState =
    new State<DeleteDataLibraryCollectionApiResponse>();

  private lastTableState: TableStateEvent = {
    searchText: '',
    paginationEvent: { pageNumber: 1, pageSize: 100 },
  };

  readonly columnDefs: ColumnDef[] = [
    {
      title: 'Name',
      key: 'name',
      type: 'text',
    },
    {
      title: 'Key',
      type: 'custom',
      component: DataLibraryKeyCellComponent,
    },
    {
      title: 'Type',
      type: 'custom',
      component: DataLibraryTypeCellComponent,
    },
    {
      title: 'Value',
      type: 'custom',
      component: DataLibraryValueCellComponent,
    },
    {
      title: 'Updated',
      key: 'updatedAt',
      type: 'date',
    },
    {
      title: 'Actions',
      type: 'actions',
      actionsConfig: {
        iconActions: [
          {
            actionKey: 'edit',
            iconPath: 'assets/icons/square-edit-outline.svg',
            label: 'Edit',
          },
          {
            actionKey: 'delete',
            iconPath: 'assets/icons/delete-outline.svg',
            label: 'Delete',
          },
        ],
      },
    },
  ];

  readonly newTypeItems: DataLibraryNewTypeItem[] = [
    {
      id: 'text',
      label: 'Text',
      iconSrc: 'assets/icons/text-string.svg',
      iconClass: dataLibraryTypeIconClass('text'),
    },
    {
      id: 'number',
      label: 'Number',
      iconSrc: 'assets/icons/numeric.svg',
      iconClass: dataLibraryTypeIconClass('number'),
    },
    {
      id: 'date',
      label: 'Date',
      iconSrc: 'assets/icons/calendar-blank-outline.svg',
      iconClass: dataLibraryTypeIconClass('date'),
    },
    {
      id: 'boolean',
      label: 'Boolean',
      iconSrc: 'assets/icons/toggle-switch-outline.svg',
      iconClass: dataLibraryTypeIconClass('boolean'),
    },
    {
      id: 'collection',
      label: 'Collection',
      iconSrc: 'assets/icons/view-sequential-outline.svg',
      iconClass: dataLibraryTypeIconClass('collection'),
    },
  ];

  onTableStateChanged(event: TableStateEvent): void {
    this.lastTableState = event;
    this.loadItems(event);
  }

  onTableAction(event: TableActionEvent): void {
    const item = event.item as DataLibraryListItemResponse;

    if (event.actionKey === 'edit') {
      this.onEdit(item);
      return;
    }

    if (event.actionKey === 'delete') {
      this.onDelete(item);
    }
  }

  onRowClicked(item: DataLibraryListItemResponse): void {
    if (item?.kind !== 'collection') {
      return;
    }
    void this.router.navigate(['/data-library/collections', item.id]);
  }

  onNewTypeSelected(id: DataLibraryNewTypeId): void {
    if (id === 'collection') {
      void this.router.navigate(['/data-library/collections/new']);
      return;
    }

    if (!VARIABLE_TYPES.has(id)) {
      return;
    }

    const dialogRef = this.overlayService.openModal(
      CreateVariableOverlayComponent,
      {
        data: { valueType: id as CreateVariableValueType },
        panelClass: ['create-variable-overlay-panel'],
      },
    );

    dialogRef.closed.subscribe(
      (result: CreateVariableOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadItems(this.lastTableState);
      },
    );
  }

  private onEdit(item: DataLibraryListItemResponse): void {
    if (item.kind === 'collection') {
      void this.router.navigate(['/data-library/collections', item.id]);
      return;
    }

    if (item.kind !== 'variable' || !item.valueType) {
      return;
    }

    const dialogRef = this.overlayService.openModal(
      EditVariableOverlayComponent,
      {
        data: {
          id: item.id,
          name: item.name,
          valueType: item.valueType as EditVariableValueType,
          value: item.value,
        },
        panelClass: ['edit-variable-overlay-panel'],
      },
    );

    dialogRef.closed.subscribe(
      (result: EditVariableOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.loadItems(this.lastTableState);
      },
    );
  }

  private onDelete(item: DataLibraryListItemResponse): void {
    if (item.kind === 'collection') {
      const dialogRef = this.overlayService.openModal(ConfirmOverlayComponent, {
        data: {
          title: 'Delete collection',
          message: `Are you sure you want to delete “${item.name}” and all of its rows? This cannot be undone.`,
          confirmLabel: 'Delete',
        },
        panelClass: ['confirm-overlay-panel'],
        fitContent: true,
      });

      dialogRef.closed.subscribe((confirmed: boolean | undefined) => {
        if (!confirmed) {
          return;
        }

        this.executeRequest({
          state: this.deleteCollectionState,
          request: this.userAPIService.deleteDataLibraryCollection(item.id),
          showLoader: true,
          handleError: true,
          onSuccess: () => {
            this.toasterService.success('Collection deleted.');
            this.loadItems(this.lastTableState);
          },
        });
      });
      return;
    }

    const dialogRef = this.overlayService.openModal(
      DeleteVariableOverlayComponent,
      {
        data: {
          id: item.id,
          name: item.name,
        },
        panelClass: ['delete-variable-overlay-panel'],
        fitContent: true,
      },
    );

    dialogRef.closed.subscribe(
      (result: DeleteVariableOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.toasterService.success('Variable deleted.');
        this.loadItems(this.lastTableState);
      },
    );
  }

  private loadItems(event: TableStateEvent): void {
    const search = event.searchText?.trim();
    const pageNumber = event.paginationEvent?.pageNumber ?? 1;
    const pageSize = event.paginationEvent?.pageSize ?? 100;

    this.executeRequest({
      state: this.tableState,
      request: this.userAPIService.listDataLibrary({
        ...(search ? { search } : {}),
        pageNumber,
        pageSize,
      }),
      handleError: true,
    });
  }
}
